import { useCallback, useState, useEffect } from "react";
import { auth } from "../firebaseConfig";
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword, signOut as firebaseSignOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { employerApi } from "../services/apiClient";
import { DateTime } from "luxon";
import { useCompanyContext } from "../context/CompanyContext";

export const useAuth = () => {
    const navigate = useNavigate();
    const { initializeCompanies } = useCompanyContext();
    const [currentUser, setCurrentUser] = useState<any>(() => {
        const firebaseToken = localStorage.getItem("firebaseToken");
        const savedUserData = localStorage.getItem("userData");

        // Only initialize user if we have the correct token type
        return (firebaseToken && savedUserData) ? JSON.parse(savedUserData) : undefined;
    });
    const [signingIn, setSigningIn] = useState(false);
    const [logoutInProgress, setLogoutInProgress] = useState(false);

    let tokenCheckPromise: Promise<unknown> | null = null;

    useEffect(() => {
        let mounted = true;

        const initAuth = async () => {
            console.log("🔍 initAuth: Starting authentication initialization");

            // Cleanup legacy session token if it exists (from other apps potentially sharing domain/port in dev)
            if (localStorage.getItem("sessionToken")) {
                console.log("Cleaning up legacy session token");
                localStorage.removeItem("sessionToken");
            }

            const firebaseToken = localStorage.getItem("firebaseToken");
            const savedUserData = localStorage.getItem("userData");

            if (firebaseToken && savedUserData && mounted) {
                console.log("✅ initAuth: Found existing token, restoring user");
                const userData = JSON.parse(savedUserData);
                setCurrentUser(userData);

                // Initialize companies if available in saved user data
                const savedCompanies = userData?.companies;
                console.log('🏢 useAuth: Checking for companies in saved userData:', savedCompanies);
                console.log('🏢 useAuth: savedCompanies type:', typeof savedCompanies, 'isArray:', Array.isArray(savedCompanies));

                if (savedCompanies && Array.isArray(savedCompanies) && savedCompanies.length > 0) {
                    console.log('🏢 useAuth: ✅ Initializing companies from saved data:', savedCompanies);
                    initializeCompanies(savedCompanies);
                } else {
                    console.warn('🏢 useAuth: ⚠️ No companies found in saved data');
                    // If user is authenticated but no companies, fetch them from API
                    // This handles the case where companies weren't saved properly or user data is stale
                    if (firebaseToken && mounted) {
                        console.log('🏢 useAuth: 🔄 Fetching fresh companies from API...');
                        employerApi.get('/authenticate', {
                            headers: {
                                Authorization: `Bearer ${firebaseToken}`
                            }
                        }).then((authResponse: any) => {
                            console.log('🏢 useAuth: API response received:', authResponse);
                            const authUserData = authResponse?.data || authResponse;
                            const authCompanies = authUserData?.companies;
                            console.log('🏢 useAuth: Companies from API:', authCompanies);

                            if (authCompanies && Array.isArray(authCompanies) && authCompanies.length > 0) {
                                console.log('🏢 useAuth: ✅ Fetched companies from API:', authCompanies);
                                initializeCompanies(authCompanies);
                                // Update saved user data with companies
                                const updatedUserData = { ...userData, companies: authCompanies };
                                setCurrentUser(updatedUserData);
                                localStorage.setItem("userData", JSON.stringify(updatedUserData));
                            } else {
                                console.error('🏢 useAuth: ❌ API returned no companies:', authResponse);
                            }
                        }).catch((error) => {
                            console.error('🏢 useAuth: ❌ Failed to fetch companies:', error);
                        });
                    }
                }
            } else if (mounted) {
                console.log("ℹ️  initAuth: No token found, clearing state silently (no redirect)");
                // Don't call handleLogout if no user - just clear state silently
                // This prevents redirect on public pages like /register
                setCurrentUser(null);
                localStorage.removeItem("firebaseToken");
                localStorage.removeItem("userData");
            }
        };

        initAuth();

        return () => {
            mounted = false;
        };
    }, []);

    const authenticateUser = async (user: any) => {
        console.log(user)
        if (user) {
            setSigningIn(true);

            try {
                const firebaseToken = await auth.currentUser?.getIdToken();

                if (!firebaseToken) {
                    throw new Error('Failed to get Firebase token');
                }

                // Call employer API authentication endpoint
                console.log('🏢 useAuth: Calling /authenticate endpoint...');
                const response = await employerApi.get('/authenticate', {
                    headers: {
                        Authorization: `Bearer ${firebaseToken}`
                    }
                });
                console.log('🏢 useAuth: /authenticate response received:', response);

                // Store Firebase token
                localStorage.setItem("firebaseToken", firebaseToken);
                console.log('🏢 useAuth: Raw authentication response:', response);
                console.log('🏢 useAuth: Response keys:', Object.keys(response || {}));

                // Backend returns: res.json({ success: true, data: responseData })
                // apiClient.get() returns: axios response.data = { success: true, data: responseData }
                // So response = { success: true, data: responseData }
                // Therefore: response.data = responseData (which contains companies)

                // Extract user data - response.data is the actual data object from backend
                const userData = response?.data || response;
                console.log('🏢 useAuth: Extracted user data:', userData);
                console.log('🏢 useAuth: userData keys:', Object.keys(userData || {}));
                console.log('🏢 useAuth: userData.companies:', userData?.companies);
                console.log('🏢 useAuth: userData.companies type:', typeof userData?.companies);
                console.log('🏢 useAuth: userData.companies isArray:', Array.isArray(userData?.companies));

                setCurrentUser(userData);
                localStorage.setItem("userData", JSON.stringify(userData));

                // Initialize companies from authentication response
                // Companies should be directly on userData (which is response.data)
                const companies = userData?.companies;
                console.log('🏢 useAuth: Final companies value:', companies);
                console.log('🏢 useAuth: Companies type:', typeof companies, 'Is array:', Array.isArray(companies), 'Length:', companies?.length);

                if (companies && Array.isArray(companies) && companies.length > 0) {
                    console.log('🏢 useAuth: ✅ Initializing companies:', companies);
                    initializeCompanies(companies);
                } else {
                    console.error('🏢 useAuth: ❌ No companies found!', {
                        'response structure': {
                            success: response?.success,
                            hasData: !!response?.data,
                            'response.data': response?.data,
                            'response.data.companies': response?.data?.companies,
                        },
                        'userData structure': {
                            'userData': userData,
                            'userData.companies': userData?.companies,
                        },
                        fullResponse: response
                    });
                }

                navigate("/employees");
                setSigningIn(false);
            } catch (error) {
                console.error(error);
                toast("Invalid login attempt. You may not have permission to access this portal.", { type: "error" });
                setSigningIn(false);
                // Ensure we sign out from firebase if the backend check fails
                firebaseSignOut(auth);
            }
        }
    };

    const handleLogout = () => {
        console.log("🚪 handleLogout: Logout initiated");

        // Prevent multiple simultaneous logout calls
        if (logoutInProgress) {
            console.log("⚠️  Logout already in progress, skipping");
            return;
        }

        setCurrentUser(null);
        setLogoutInProgress(true);
        firebaseSignOut(auth)
            .then(() => {
                toast("You have been signed out!", { type: "info" });
                localStorage.removeItem("firebaseToken");
                localStorage.removeItem("cachedApplications");
                localStorage.removeItem("applicationsTimestamp");
                localStorage.removeItem("userData");
                localStorage.removeItem("sessionToken"); // Also cleanup session token
                localStorage.removeItem("activeCompanyId"); // Cleanup active company
                setCurrentUser(null);
                setSigningIn(false);
                navigate("/login");
            })
            .catch(error => {
                console.error("Error signing out: ", error);
            })
            .finally(() => {
                setTimeout(() => {
                    setLogoutInProgress(false);
                }, 5000);
            });
    };

    const signInWithGoogle = useCallback(() => {
        setSigningIn(true);
        const provider = new GoogleAuthProvider();
        signInWithPopup(auth, provider)
            .then((result) => {
                const user = result.user;
                authenticateUser(user);
            })
            .catch((error) => {
                console.error("SignIn with Google error:", error.message);
                setSigningIn(false);
            });
    }, []);

    const signInWithEmailAndPassword = (email: string, password: string) => {
        setSigningIn(true);
        firebaseSignInWithEmailAndPassword(auth, email, password)
            .then((userCredential) => {
                const user = userCredential.user;
                authenticateUser(user);
            })
            .catch((error) => {
                setSigningIn(false);
                toast("Login failed.", { type: "error" });
                console.error(error.code, error.message);
            });
    };

    const checkTokenAndProceed = async () => {
        if (tokenCheckPromise) {
            console.log("Check already in progress - cancelling this one")
            return tokenCheckPromise; // If a check is already in progress, return the same promise
        }

        tokenCheckPromise = new Promise(async (resolve, reject) => {

            if (isTokenExpired()) {

                handleLogout();
                reject(new Error('Token expired'));
            } else {
                resolve(null); // Token is valid, proceed with requests
            }
        });

        try {
            await tokenCheckPromise;
        } finally {
            tokenCheckPromise = null; // Reset after the check is complete
        }
    };

    const isTokenExpired = () => {
        console.log("Checking if Firebase token is expired")

        const token = localStorage.getItem('firebaseToken');
        if (!token) return true;

        try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const expiryDate = DateTime.fromSeconds(tokenPayload.exp);
            return expiryDate < DateTime.now();
        } catch (e) {
            console.error("Failed to parse Firebase token:", e);
            return true;
        }
    };

    return {
        currentUser,
        setCurrentUser,
        signingIn,
        setSigningIn,
        signInWithGoogle,
        signInWithEmailAndPassword,
        signOut: handleLogout,
        logoutInProgress,
        checkTokenAndProceed,
    };
};
